import Axios, {
    type AxiosError,
    type AxiosInstance,
    type AxiosRequestConfig,
    type CustomParamsSerializer,
    HttpStatusCode,
    ParamEncoder
} from 'axios'
import type {
    GlxAxiosHttpError,
    GlxAxiosRequestConfig,
    GlxAxiosResponse,
    RequestMethods
} from './ai-types.d'

import AiAuthUtil from '@/utils/AiAuthUtil'

import {stringify, parse} from 'qs'
import {GlxResult} from '@/ai-plugin-api/type'

// 相关配置请参考：www.axios-js.com/zh-cn/docs/#axios-request-config-1
export const defaultConfig: AxiosRequestConfig = {
    baseURL: import.meta.env.VITE_AI_PLUGIN_API_BASE_URL,
    // baseURL: '/ai-api',
    withCredentials: true,
    // 请求超时时间
    timeout: 10000,
    headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
    },
    // 数组格式参数序列化（https://github.com/axios/axios/issues/5142）
    paramsSerializer: {
        encode: parse as ParamEncoder,
        serialize: stringify as unknown as CustomParamsSerializer
    }
}

class GlxHttp {
    constructor() {
        this.httpInterceptorsRequest()
        this.httpInterceptorsResponse()
    }

    /** `token`过期后，暂存待执行的请求 */
    private static requests = []

    /** 防止重复刷新`token` */
    private static isRefreshing = false

    /** 初始化配置对象 */
    private static initConfig: GlxAxiosRequestConfig = {}

    /** 保存当前`Axios`实例对象 */
    private static axiosInstance: AxiosInstance = Axios.create(defaultConfig)

    /** 重连原始请求 */
    // private static retryOriginalRequest(config: GlxAxiosRequestConfig<any>) {
    //   return new Promise((resolve) => {
    //     GlxHttp.requests.push((token: string) => {
    //       config.headers['Authorization'] = formatToken(token)
    //       resolve(config)
    //     })
    //   })
    // }

    /** 请求拦截 */
    private httpInterceptorsRequest(): void {
        GlxHttp.axiosInstance.interceptors.request.use(
            async (config: GlxAxiosRequestConfig): Promise<any> => {
                // 优先判断post/get等方法是否传入回调，否则执行初始化设置等回调
                if (typeof config.beforeRequestCallback === 'function') {
                    config.beforeRequestCallback(config)
                    return config
                }
                if (GlxHttp.initConfig.beforeRequestCallback) {
                    GlxHttp.initConfig.beforeRequestCallback(config)
                    return config
                }

                const tokenWithBearerPrefix =
                    AiAuthUtil.getTokenWithBearerPrefix()
                if (tokenWithBearerPrefix) {
                    if (!config.headers) {
                        config.headers = {}
                    }
                    config.headers['Authorization'] = tokenWithBearerPrefix
                }
                return config
            },
            (error: AxiosError) => {
                console.error(error)
                return Promise.reject(error)
            }
        )
    }

    /** 响应拦截 */
    private httpInterceptorsResponse(): void {
        const instance = GlxHttp.axiosInstance
        instance.interceptors.response.use(
            (response: GlxAxiosResponse) => {
                const $config = response.config
                // 优先判断post/get等方法是否传入回调，否则执行初始化设置等回调
                if (typeof $config.beforeResponseCallback === 'function') {
                    $config.beforeResponseCallback(response)
                    return response
                }
                if (GlxHttp.initConfig.beforeResponseCallback) {
                    GlxHttp.initConfig.beforeResponseCallback(response)
                    return response
                }
                return response
            },
            async (error: GlxAxiosHttpError) => {
                console.error(error)

                // 003 诊断埋点：AI 插件链路请求失败进白名单日志（diagCollector
                // 收集；开关关闭时 console hook 不存在，仅多一行普通日志，零成本）
                try {
                    const cfg = error?.config
                    console.log(`[Http] ai-plugin fail ${error?.response?.status ?? 'NET'} ${(cfg?.method || '?').toUpperCase()} ${cfg?.url || '?'}`)
                } catch {
                    // 埋点自身异常不影响业务错误处理
                }

                const $error = error
                $error.isCancelRequest = Axios.isCancel($error)

                const response = error.response as GlxAxiosResponse<
                    GlxResult<void>
                >
                const status = response?.status
                if (!!status) {
                    switch (status) {
                        case HttpStatusCode.Unauthorized: {
                            AiAuthUtil.removeAuth()
                            break
                        }
                        case HttpStatusCode.Forbidden:
                            break
                        default:
                            break
                    }
                }

                const data: GlxResult<void> = response?.data
                if (data) {
                    const errMsg = data.error.errMsg
                }

                // 所有的响应异常 区分来源为取消请求/非取消请求
                return Promise.reject($error)
            }
        )
    }

    /** 通用请求工具函数 */
    public request<Result, Body = any>(
        method: RequestMethods,
        url: string,
        axiosConfig?: GlxAxiosRequestConfig<Body>
    ): Promise<Result> {
        const config = {
            method,
            url,
            ...axiosConfig
        } satisfies GlxAxiosRequestConfig<Body>

        // 单独处理自定义请求/响应回调
        return new Promise((resolve, reject) => {
            GlxHttp.axiosInstance
                .request(config)
                .then((response) => {
                    // 直接返回data，不返回冗余数据
                    resolve(response.data)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    }

    /** 单独抽离的`post`工具函数 */
    public post<Result, Body = any>(
        url: string,
        config?: GlxAxiosRequestConfig<Body>
    ): Promise<Result> {
        return this.request<Result, Body>('post', url, config)
    }

    /** 单独抽离的`put`工具函数 */
    public put<Result, Body = any>(
        url: string,
        config?: GlxAxiosRequestConfig<Body>
    ): Promise<Result> {
        return this.request<Result, Body>('put', url, config)
    }

    /** 单独抽离的`get`工具函数 */
    public get<Result, Params = any>(
        url: string,
        config?: GlxAxiosRequestConfig & {
            params?: Params
        }
    ): Promise<Result> {
        return this.request<Result>('get', url, config)
    }

    /** 单独抽离的`delete`工具函数 */
    public delete<Result, Params = any>(
        url: string,
        config?: GlxAxiosRequestConfig & {
            params?: Params
        }
    ): Promise<Result> {
        return this.request<Result>('delete', url, config)
    }

    /** 单独抽离的`glx-post`工具函数 */
    public glxPost<ResultData, Body = any>(
        url: string,
        config?: GlxAxiosRequestConfig<Body>
    ): Promise<GlxResult<ResultData>> {
        return this.post<GlxResult<ResultData>, Body>(url, config)
    }

    /** 单独抽离的`glx-put`工具函数 */
    public glxPut<ResultData, Body = any>(
        url: string,
        config?: GlxAxiosRequestConfig<Body>
    ): Promise<GlxResult<ResultData>> {
        return this.put<GlxResult<ResultData>, Body>(url, config)
    }

    /** 单独抽离的`glxGet`工具函数 */
    public glxGet<ResultData, Params = any>(
        url: string,
        config?: GlxAxiosRequestConfig<void> & {
            params?: Params
        }
    ): Promise<GlxResult<ResultData>> {
        return this.get<GlxResult<ResultData>, Params>(url, config)
    }

    /** 单独抽离的`delete`工具函数 */
    public glxDelete<ResultData, Params = any>(
        url: string,
        config?: GlxAxiosRequestConfig<void> & {
            params?: Params
        }
    ): Promise<GlxResult<ResultData>> {
        return this.delete<GlxResult<ResultData>, Params>(url, config)
    }
}

export const http = new GlxHttp()
